import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Calendar, Shield, RefreshCw, Download, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const { token, user: currentUser } = useAuth();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (userId, userEmail) => {
    // Não permitir excluir o próprio usuário
    if (userEmail === currentUser?.email) {
      toast.error('Você não pode excluir sua própria conta!');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${userEmail}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    setDeleting(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuário excluído com sucesso!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setDeleting(null);
    }
  };

  const exportToCSV = () => {
    if (users.length === 0) {
      toast.error('Nenhum usuário para exportar');
      return;
    }

    const headers = ['Nome', 'Email', 'WhatsApp', 'Role', 'Data Cadastro'];
    const csvContent = [
      headers.join(','),
      ...users.map(user => [
        `"${user.name}"`,
        `"${user.email}"`,
        `"${user.whatsapp || ''}"`,
        `"${user.role}"`,
        `"${new Date(user.created_at).toLocaleDateString('pt-BR')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `usuarios_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Usuários exportados com sucesso!');
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <Badge className="bg-purple-600">Admin</Badge>;
    }
    return <Badge className="bg-blue-600">Aluno</Badge>;
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="pt-6 text-center text-gray-400">
          Carregando usuários...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white">
          Usuários Registrados ({users.length})
        </CardTitle>
        <div className="flex gap-2">
          <Button
            onClick={fetchUsers}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-white"
          >
            <RefreshCw className="mr-2" size={16} />
            Atualizar
          </Button>
          <Button
            onClick={exportToCSV}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-white"
          >
            <Download className="mr-2" size={16} />
            Exportar CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {users.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <User size={48} className="mx-auto mb-4 opacity-50" />
            <p>Nenhum usuário registrado ainda.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Nome</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">WhatsApp</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Tipo</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Cadastro</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center mr-3">
                          <span className="text-white text-sm font-semibold">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-white">{user.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <a href={`mailto:${user.email}`} className="text-gray-300 hover:text-white">
                        {user.email}
                      </a>
                    </td>
                    <td className="py-3 px-4">
                      {user.whatsapp ? (
                        <a 
                          href={`https://wa.me/55${user.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300"
                        >
                          {user.whatsapp}
                        </a>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        onClick={() => handleDelete(user.id, user.email)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        disabled={deleting === user.id || user.email === currentUser?.email}
                        title={user.email === currentUser?.email ? 'Não é possível excluir sua própria conta' : 'Excluir usuário'}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminUsers;
